import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Award, Users, Trophy, Star, Loader2, Mail, Lock, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '@/data/countries';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    company: '',
    country: ''
  });

  const handleGoogleLogin = () => {
    const redirectUrl = encodeURIComponent('https://standvision-submit.preview.emergentagent.com/dashboard');
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Use XMLHttpRequest instead of fetch to avoid response cloning issues
      const loginData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${backendUrl}/api/auth/login`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.withCredentials = true;
        
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data });
          } catch (e) {
            resolve({ status: xhr.status, data: { detail: xhr.responseText } });
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Network error'));
        };
        
        xhr.send(JSON.stringify({
          email: loginForm.email,
          password: loginForm.password
        }));
      });
      
      if (loginData.status === 200) {
        await login(null); // Trigger auth context refresh
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        // Show error message under the form fields, not as toast
        setLoginError(loginData.data.detail || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Only show toast for actual network/connection errors
      toast.error('Connection error. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: registerForm.email,
          password: registerForm.password,
          name: registerForm.name,
          phone: registerForm.phone,
          company: registerForm.company,
          country: registerForm.country
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await login(null); // Trigger auth context refresh
        toast.success('Registration successful! Welcome!');
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/" className="inline-block mb-8">
              <div className="flex items-center justify-center gap-2">
                <Award className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold">Stand Vision Awards</span>
              </div>
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="login-heading">
              Sign In to Your Account
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Access your dashboard, manage submissions, and track your award journey
            </p>
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Login/Register Card */}
            <Card className="p-8 shadow-xl">
              <Tabs defaultValue="email" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="email" data-testid="email-tab">Email</TabsTrigger>
                  <TabsTrigger value="google" data-testid="google-tab">Google</TabsTrigger>
                </TabsList>
                
                {/* Email/Password Tab */}
                <TabsContent value="email" className="space-y-4">
                  <Tabs defaultValue="login" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="login">Sign In</TabsTrigger>
                      <TabsTrigger value="register">Sign Up</TabsTrigger>
                    </TabsList>
                    
                    {/* Login Form */}
                    <TabsContent value="login">
                      <form onSubmit={handleEmailLogin} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="login-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="login-email"
                              type="email"
                              placeholder="your@email.com"
                              className={`pl-10 ${loginError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              value={loginForm.email}
                              onChange={(e) => {
                                setLoginForm({...loginForm, email: e.target.value});
                                setLoginError('');
                              }}
                              required
                              data-testid="login-email-input"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="login-password">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="login-password"
                              type="password"
                              placeholder="••••••••"
                              className={`pl-10 ${loginError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              value={loginForm.password}
                              onChange={(e) => {
                                setLoginForm({...loginForm, password: e.target.value});
                                setLoginError('');
                              }}
                              required
                              data-testid="login-password-input"
                            />
                          </div>
                        </div>
                        
                        {loginError && (
                          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="login-error-message">
                            {loginError}
                          </div>
                        )}
                        
                        <Button
                          type="submit"
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          disabled={loading}
                          data-testid="email-login-button"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Signing in...
                            </>
                          ) : (
                            'Sign In'
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                    
                    {/* Register Form */}
                    <TabsContent value="register">
                      <form onSubmit={handleRegister} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="register-name">Full Name *</Label>
                          <div className="relative">
                            <UserIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="register-name"
                              type="text"
                              placeholder="John Doe"
                              className="pl-10"
                              value={registerForm.name}
                              onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                              required
                              data-testid="register-name-input"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="register-email">Email *</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="register-email"
                              type="email"
                              placeholder="your@email.com"
                              className="pl-10"
                              value={registerForm.email}
                              onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                              required
                              data-testid="register-email-input"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="register-password">Password * (At least 6 characters)</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="register-password"
                              type="password"
                              placeholder="••••••••"
                              className="pl-10"
                              value={registerForm.password}
                              onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                              required
                              minLength={6}
                              data-testid="register-password-input"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="register-confirm-password">Confirm Password *</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="register-confirm-password"
                              type="password"
                              placeholder="••••••••"
                              className="pl-10"
                              value={registerForm.confirmPassword}
                              onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                              required
                              minLength={6}
                              data-testid="register-confirm-password-input"
                            />
                          </div>
                        </div>
                        
                        <Separator className="my-4" />
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="register-phone">Phone</Label>
                            <Input
                              id="register-phone"
                              type="tel"
                              placeholder="+1 234 567 8900"
                              value={registerForm.phone}
                              onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="register-company">Company</Label>
                            <Input
                              id="register-company"
                              type="text"
                              placeholder="Company Name"
                              value={registerForm.company}
                              onChange={(e) => setRegisterForm({...registerForm, company: e.target.value})}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="register-country">Country</Label>
                          <Select 
                            value={registerForm.country} 
                            onValueChange={(value) => setRegisterForm({...registerForm, country: value})}
                          >
                            <SelectTrigger id="register-country">
                              <SelectValue placeholder="Select your country" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {COUNTRIES.map((country) => (
                                <SelectItem key={country} value={country}>
                                  {country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <Button
                          type="submit"
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          disabled={loading}
                          data-testid="email-register-button"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Registering...
                            </>
                          ) : (
                            'Sign Up'
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </TabsContent>
                
                {/* Google Tab */}
                <TabsContent value="google" className="space-y-4">
                  <div className="text-center py-4">
                    <p className="text-gray-600 mb-6">Sign in quickly with your Google account</p>
                    
                    <Button
                      onClick={handleGoogleLogin}
                      className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg"
                      data-testid="google-signin-button"
                    >
                      <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Sign In with Google
                    </Button>
                    
                    <p className="text-sm text-gray-500 mt-4">
                      Your account will be created automatically if you don't have one
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </Card>

            {/* Benefits Card */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold">Why Create an Account?</h3>
              
              <Card className="p-6 border-l-4 border-orange-500">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Award className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Submit Your Work</h4>
                    <p className="text-gray-600">Enter your exhibition stands for award consideration</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-orange-500">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Trophy className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Track Your Applications</h4>
                    <p className="text-gray-600">Monitor the status of your submissions in real-time</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-orange-500">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Star className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Manage Your Profile</h4>
                    <p className="text-gray-600">Keep your information up to date and showcase your achievements</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-orange-500">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Join the Community</h4>
                    <p className="text-gray-600">Connect with other designers and industry professionals</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Need help? <Link to="/" className="text-orange-500 hover:text-orange-600 font-medium">Visit our homepage</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
